import * as htmlToImage from "html-to-image";
import * as React from "react";
import { IThumbnailGeneratorProps } from ".";
import { ThumbnailContainerDiv, ThumbnailIFrame } from "./ThumbnailGenerator.styles";

export const ThumbnailGenerator: React.FC<IThumbnailGeneratorProps> =
    ({ Html, OnThumbnailGenerated, TemplateEvents }: IThumbnailGeneratorProps) => {

        const thumbnailRef = React.useRef<HTMLIFrameElement>(null);

        // Updates specific elements in order to be used in the thumbnail
        const updateHtml = (html: string): Promise<string> => {

            // Remove comments from HTML - invalid when generating image
            html = html.replace(/<!--\[if\s(?:[^<]+|<(?!!\[endif\]-->))*<!\[endif\]-->/gm, "");

            return new Promise((resolve, reject) => {
                try {
                    const htmlDiv = document.createElement("div");
                    htmlDiv.innerHTML = html;

                    // Add anonymous crossorigin access for css styles
                    const linkElements = htmlDiv.getElementsByTagName("link");
                    addCrossOriginAttributes(linkElements);

                    // Convert images to dataUrls
                    const imgElements = htmlDiv.getElementsByTagName("img");

                    if (imgElements.length > 0) {
                        const imgPromises: Array<Promise<void>> = [];

                        // Loop the images and convert them to dataUrls
                        for (let i = 0; i < imgElements.length; i++) {
                            if (imgElements[i] instanceof HTMLImageElement) {
                                imgPromises.push(getImage(imgElements[i]));
                            }
                        }

                        Promise.all(imgPromises).then(() => {
                            resolve(htmlDiv.outerHTML);
                        }).catch(() => {
                            reject();
                        });
                    } else {
                        resolve(htmlDiv.outerHTML);
                    }
                } catch {
                    reject();
                }
            });
        };

        const addCrossOriginAttributes = (elements: HTMLCollectionBase) => {
            if (elements.length) {
                for (let i = 0; i < elements.length; i++) {
                    elements[i].setAttribute("crossorigin", "anonymous");
                }
            }
        };

        const getImage = (imgElement: HTMLImageElement): Promise<void> => {
            return new Promise((resolve, reject) => {
                if (!imgElement) {
                    resolve();
                }

                // Create an image in memory
                const tempImg = new Image();
                tempImg.setAttribute("crossorigin", "anonymous");
                tempImg.onload = () => {

                    const canvas = document.createElement("canvas");
                    canvas.width = tempImg.width;
                    canvas.height = tempImg.height;

                    const ctx = canvas.getContext("2d");
                    if (ctx) {
                        ctx.drawImage(tempImg, 0, 0);
                        imgElement.src = canvas.toDataURL("image/png");
                        resolve();
                    } else {
                        reject();
                    }
                };
                tempImg.onerror = () => {
                    const width = imgElement.width;
                    const height = imgElement.height;

                    imgElement.src = "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="; // Transparent pixel
                    imgElement.style.width = `${width}px`;
                    imgElement.style.height = `${height}px`;
                    imgElement.style.opacity = "0";
                    resolve();
                };

                // Get the image key if one exists, if it's a guid (regex) then assume its stored in AzureStorage
                const guids: RegExpMatchArray | null = imgElement.src.match(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
                if (guids && guids.length) {
                    tempImg.src = TemplateEvents.getTemplateImage(guids[guids.length - 1]);
                } else {
                    tempImg.src = imgElement.src;
                }
            });
        };

        React.useEffect(() => {
            if (Html) {
                updateHtml(Html).then((thumbnailHtml) => {
                    if (thumbnailRef?.current?.contentWindow && thumbnailRef.current.contentDocument) {
                        thumbnailRef.current.contentWindow.document.open();
                        thumbnailRef.current.contentWindow.document.write(thumbnailHtml);
                        thumbnailRef.current.contentWindow.document.close();
                        thumbnailRef.current.contentDocument.body.onload = iframeLoaded;
                    }
                }).catch(() => {
                    if (OnThumbnailGenerated) {
                        OnThumbnailGenerated(null);
                    }
                });
            }
        }, [Html]);

        const generateThumbnail = async (): Promise<string | null> => {
            if (thumbnailRef?.current?.contentDocument) {
                // Generate the image
                return htmlToImage.toPng(thumbnailRef.current.contentDocument.body, {
                    width: 800,
                    height: 1000,
                });
            }
            return null;
        };

        const resizeThumbnail = async (dataUrl: string): Promise<string | null> => {

            return new Promise((resolve) => {

                const tempImg = new Image();
                tempImg.onload = () => {
                    const canvas = document.createElement("canvas");
                    canvas.width = 300;
                    canvas.height = 375;

                    const ctx = canvas.getContext("2d");
                    if (ctx) {
                        ctx.drawImage(tempImg, 0, 0, canvas.width, canvas.height);
                        resolve(canvas.toDataURL("image/png"));
                    } else {
                        resolve(null);
                    }
                };
                tempImg.onerror = () => {
                    resolve(null);
                };

                tempImg.src = dataUrl;
            });
        };

        const iframeLoaded = () => {
            try {
                generateThumbnail().then((dataUrl: string | null) => {
                    if (dataUrl && OnThumbnailGenerated) {
                        resizeThumbnail(dataUrl).then((resizedDataUrl: string | null) => {
                            OnThumbnailGenerated(resizedDataUrl);
                        }).catch(() => {
                            if (OnThumbnailGenerated) {
                                OnThumbnailGenerated(null);
                            }
                        });
                    }
                }).catch(() => {
                    if (OnThumbnailGenerated) {
                        OnThumbnailGenerated(null);
                    }
                });
            } catch {
                if (OnThumbnailGenerated) {
                    OnThumbnailGenerated(null);
                }
            }
        };

        return (
            <ThumbnailContainerDiv>
                <ThumbnailIFrame
                    ref={thumbnailRef}
                    sandbox="allow-same-origin allow-popups"
                />
            </ThumbnailContainerDiv>
        );
    };
